import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button, LinearProgress, ListItemButton, Typography } from '@mui/material'
import { useState, useEffect, useContext, useRef } from 'react'

import HelpIcon from '../assets/icons/help-icon.svg'
import { Document, FormError, SystemUser, UserSessionType } from '../types'
import Dialog from '../components/common/dialog'
import { requestAccessToken } from '../api/auth.api'
import NextIcon from '../assets/icons/next-icon.svg'
import { AuthContext } from '../contexts/auth.context'
import { UserContext } from '../contexts/user.context'
import Pagination from '../components/common/pagination'
import {deleteDocumentData, resolveDocumentsAccessToken } from '../api/documents.api'
import { AppStateContext } from '../contexts/app-state.context'
import { NotificationsContext } from '../contexts/notifications.context'
import ActionsBar from '../components/documents-completed-management/actions-bar'
import { FiltersOptionsModal } from '../components/common/filters-options-modal'
import { ProcessingDocumentWarning } from '../components/common/processing-document-warning'
import { handleDeleteDataByOne } from '../utils/db/common-methods'
import DocumentsCompletedListItem from '../components/documents-completed-management/documents-completed-list-item'
import { normalizeDocumentData, upsertEntityWithLimitAtStart, upsertManyWithLimitAtStartFast } from '../utils'
import { getBatchesCompletedData } from '../api/app-data.api'
import { useSocket } from '../hooks/use-socket'
import { countriesCurrencies } from '../lib'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { searchBatchesByRegex } from '../api/app-queries.api'

const DocumentsCompletedManagement = () => {
  const batchesCompletedStatus = ['dispersing', 'register-disperse-completed', 'completed']
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { setScrollToTop, processingBatch } = useContext(AppStateContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['documents-completed-management', 'common'])

  const currentListPageRef = useRef<number>(1)
  const documentToSearchRef = useRef<string>('')
  const dataFullyLoadedRef = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const documentsToDeleteRef = useRef<Array<string>>([])
  const currentDocumentsDataRef = useRef<Array<Document>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const currentShowDeleteButtonFromActionsBarRef = useRef<boolean>(false)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [documentToSearch, setDocumentToSearch] = useState<string>('')
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [documentsList, setDocumentsList] = useState<Array<Document>>([])
  const [documentsData, setDocumentsData] = useState<Array<Document>>([])
  const [selectAllDocuments, setSelectAllDocuments] = useState<boolean>(false)
  const [systemUsersData, setSystemUsersData] = useState<Array<SystemUser>>([])
  const [documentsToDelete, setDocumentsToDelete] = useState<Array<string>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [selectedDocument, setSelectedDocument] = useState<Partial<Document>>({})
  const [searchedDocuments, setSearchedDocuments] = useState<Array<Document>>([])
  const [openFiltersOptionsModal, setOpenFiltersOptionsModal] = useState<boolean>(false)
  const [openDeleteDocumentModal, setOpenDeleteDocumentModal] = useState<boolean>(false)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [dialogDeleteDocumentIsLoading, setDialogDeleteDocumentIsLoading] = useState<boolean>(false)
  const [showDeleteButtonFromActionsBar, setShowDeleteButtonFromActionsBar] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`addNewBatchCompleted:${selectedCountry}`, async (dataFromSocket: any) => {
      if (!batchesCompletedStatus.includes(dataFromSocket.status) && !dataFromSocket.status.includes('error')) {
        const batchesCompletedDataUpdated = currentDocumentsDataRef.current.filter((documentData: Document) => String(documentData._id) !== String(dataFromSocket._id))
        globalThis.sessionStorage.setItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesCompletedData: batchesCompletedDataUpdated }))

        handleSetInitialData({ batchesCompletedData: batchesCompletedDataUpdated })
        return
      }

      const batchesCompletedDataUpdated = upsertEntityWithLimitAtStart(currentDocumentsDataRef.current, dataFromSocket, 500)
      globalThis.sessionStorage.setItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesCompletedData: batchesCompletedDataUpdated }))

      handleSetInitialData({ batchesCompletedData: batchesCompletedDataUpdated })
    })

    appDataSocket.on(`updateBatchCompleted:${selectedCountry}`, async (dataFromSocket: any) => {
      if (
        (!batchesCompletedStatus.includes(dataFromSocket.status) && !dataFromSocket.status.includes('error')) ||
        ((batchesCompletedStatus.includes(dataFromSocket.status) || dataFromSocket.status.includes('error')) && dataFromSocket.deleted === true)
      ) {
        const batchesCompletedDataUpdated = currentDocumentsDataRef.current.filter((documentData: Document) => String(documentData._id) !== String(dataFromSocket._id))
        globalThis.sessionStorage.setItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesCompletedData: batchesCompletedDataUpdated }))

        handleSetInitialData({ batchesCompletedData: batchesCompletedDataUpdated })
        return
      }

      const batchesCompletedDataUpdated = upsertEntityWithLimitAtStart(currentDocumentsDataRef.current, dataFromSocket, 500)
      globalThis.sessionStorage.setItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesCompletedData: batchesCompletedDataUpdated }))

      handleSetInitialData({ batchesCompletedData: batchesCompletedDataUpdated })
    })

    appDataSocket.on(`deleteBatchCompleted:${selectedCountry}`, async (dataFromSocket: any) => {
      const batchesCompletedDataUpdated = currentDocumentsDataRef.current.filter((documentData: Document) => String(documentData._id) !== String(dataFromSocket.documentKey))
      globalThis.sessionStorage.setItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesCompletedData: batchesCompletedDataUpdated }))

      handleSetInitialData({ batchesCompletedData: batchesCompletedDataUpdated })
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetBatchesCompletedData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (documentsToDelete.length === 0) {
      currentShowDeleteButtonFromActionsBarRef.current = false
      setShowDeleteButtonFromActionsBar(false)
    } else {
      if (!selectedDocument.originalName) {
        currentShowDeleteButtonFromActionsBarRef.current = true
        setShowDeleteButtonFromActionsBar(true)
      }
    }
  }, [documentsToDelete, selectedDocument])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const documentsPerPage = documentToSearch === '' ? documentsData.slice(firstPageIndex, lastPageIndex) : searchedDocuments.slice(firstPageIndex, lastPageIndex)

    setDocumentsList(documentsPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, documentsData, searchedDocuments])

  const handleGetBatchesCompletedData = async () => {
    let localBatchesCompletedDataToSet: any = {}
    const localBatchesCompletedData = globalThis.sessionStorage.getItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`)

    if (localBatchesCompletedData) {
      localBatchesCompletedDataToSet = JSON.parse(localBatchesCompletedData)
      handleSetInitialData(localBatchesCompletedDataToSet)
    }

    handleGetBatchesCompletedDataFromRemote()
  }

  const handleGetBatchesCompletedDataFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getBatchesCompletedData(selectedCountry, accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetBatchesCompletedData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const currentSelectedDocumentsIds =
      currentDocumentsDataRef.current.length === 0 ? [] : currentDocumentsDataRef.current.filter((documentData) => documentData.selected === true).map((documentData) => String(documentData._id))
    const currentListPageDataState = currentListPageRef.current
    const currentDocumentToSearchState = documentToSearchRef.current
    const documentsCompletedNormalizedData = []

    for (const element of dataToSet.batchesCompletedData) {
      const documentData = { ...element }
      const documentNormalizedData = normalizeDocumentData(documentData) as Document
      documentNormalizedData.approvedByFullName = element.approvedByFullName

      if (currentSelectedDocumentsIds.includes(String(documentNormalizedData._id))) {
        documentNormalizedData.selected = true
      } else {
        documentNormalizedData.selected = false
      }

      documentsCompletedNormalizedData.push(documentNormalizedData)
    }

    documentsCompletedNormalizedData.sort((thisDocument: Partial<Document>, nextDocument: Partial<Document>) => {
      return Date.parse(nextDocument.createdAt) - Date.parse(thisDocument.createdAt)
    })

    if (currentDocumentToSearchState === '' || currentDocumentToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const documentsPerPage = documentsCompletedNormalizedData.slice(firstPageIndex, lastPageIndex)

      handleDocumentsDeletionProcess(documentsPerPage)
      setDocumentsList(documentsPerPage)
    }

    currentDocumentsDataRef.current = documentsCompletedNormalizedData
    setSystemUsersData(dataToSet.systemUsers)
    setDocumentsData(documentsCompletedNormalizedData)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }

    dataFullyLoadedRef.current = true
  }

  const handleDocumentsDeletionProcess = (documentsPerPage: Array<Document>) => {
    const currentDocumentsToDeleteState = documentsToDeleteRef.current ?? []
    const currentShowDeleteButtonFromActionsBarState = currentShowDeleteButtonFromActionsBarRef.current
    const documentsToDeleteOutOfPage = []

    for (const element of currentDocumentsToDeleteState) {
      const documentToDeleteId = element
      const findDocumentIndex = documentsPerPage?.findIndex((documentData) => String(documentData._id) === String(documentToDeleteId))

      if (findDocumentIndex === -1) {
        documentsToDeleteOutOfPage.push(documentToDeleteId)
      }
    }

    for (const element of documentsToDeleteOutOfPage) {
      const documentId = element
      const index = currentDocumentsToDeleteState.indexOf(documentId)
      if (index > -1) {
        currentDocumentsToDeleteState.splice(index, 1)
      }
    }

    documentsToDeleteRef.current = currentDocumentsToDeleteState
    if (documentsToDeleteRef.current.length < pageSize) {
      setSelectAllDocuments(false)
    }

    if (documentsToDeleteRef.current.length === 0) {
      setShowDeleteButtonFromActionsBar(false)
    } else {
      setShowDeleteButtonFromActionsBar(currentShowDeleteButtonFromActionsBarState)
    }

    setTimeout(() => {
      setDocumentsToDelete(currentDocumentsToDeleteState)
    }, 50)
  }

  const handleUncheckAllDocuments = () => {
    setSelectAllDocuments(false)
    setDocumentsData((currentDocumentsState) => {
      const documentsStateUpdated = [...currentDocumentsState]
      const documentsStateUpdatedToReturn = documentsStateUpdated.map((documentData) => ({
        ...documentData,
        selected: false
      }))

      currentDocumentsDataRef.current = documentsStateUpdatedToReturn
      return documentsStateUpdatedToReturn
    })
  }

  const handleDiscardDeletionProcess = () => {
    setDocumentsToDelete([])
    handleUncheckAllDocuments()
    documentsToDeleteRef.current = []
    setDialogDeleteDocumentIsLoading(false)
    const currentDocumentToSearchState = documentToSearchRef.current

    if (currentDocumentToSearchState !== '') {
      handleSetDocumentToSearch('', false)
    }
  }

  const handleSetDocumentToSearch = (documentToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    documentToSearchRef.current = documentToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setNoRegistriesFoundAtLocal(false)
    setDocumentToSearch(documentToSearchData)

    if (documentToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentDocumentsDataState = currentDocumentsDataRef.current ?? []
      const documentsPerPage = currentDocumentsDataState.slice(firstPageIndex, lastPageIndex)

      setDocumentsList(documentsPerPage)
      setSearchedDocuments([])

      if (showError) setErrors({ documentToSearch: t('searchField.errors.emptyField') })
      return
    }

    const currentFullDocumentsData = [...(currentDocumentsDataRef.current as Array<Document>)]
    const filteredDocuments = currentFullDocumentsData.filter((document) => {
      const documentSystemUserFullName = document.uploadedByFullName
      const documentStatus =
        document.status.indexOf('error') !== -1
          ? t('documentStatus.error')
          : document.status === 'dispersing' || document.status === 'register-disperse-completed'
          ? t('documentStatus.dispersing')
          : t('documentStatus.completed')

      if (document.selected === true) document.selected = false

      return (
        documentStatus.toLowerCase().includes(documentToSearchData.toLowerCase()) ||
        documentSystemUserFullName?.toLowerCase().includes(documentToSearchData.toLowerCase()) ||
        document.originalName.toLowerCase().includes(documentToSearchData.toLowerCase())
      )
    })

    documentsToDeleteRef.current = []
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const documentsPerPage = filteredDocuments.slice(firstPageIndex, lastPageIndex)

    setDocumentsToDelete([])
    handleUncheckAllDocuments()
    setSelectAllDocuments(false)
    setDocumentsList(documentsPerPage)
    setSearchedDocuments(filteredDocuments)

    if (filteredDocuments.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleChangeStateToOpenModal = (selectedDocumentData: Document) => {
    const currentDocumentsList = [...documentsList]
    const documentsListUpdated = currentDocumentsList.map((documentData) => ({
      ...documentData,
      selected: false
    }))

    setSelectedDocument({
      ...selectedDocumentData
    })
    setSelectAllDocuments(false)
    // setUncheckAllDocuments(true)
    setDocumentsList(documentsListUpdated)
    setShowDeleteButtonFromActionsBar(false)
  }

  const handleConfirmDocumentDeletion = async () => {
    setErrors({})
    setDialogDeleteDocumentIsLoading(true)
    const currentDocumentsToDelete = [...documentsToDeleteRef.current]

    if (selectedDocument.originalName && !documentsToDeleteRef.current?.includes(String(selectedDocument._id))) {
      documentsToDeleteRef.current = [String(selectedDocument._id)]
      setDocumentsToDelete([String(selectedDocument._id)])
      currentDocumentsToDelete.push(String(selectedDocument._id))
    }

    for (const element of currentDocumentsToDelete) {
      const documentToDeleteId = element

      if (processingBatch === true) {
        handleDiscardDeletionProcess()
        setOpenDeleteDocumentModal(false)
        updateNewNotificationStatus?.(true, 'deletion-process-interrupted')
        break
      }

      try {
        setDialogDeleteDocumentIsLoading(true)
        const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
        await deleteDocumentData(documentToDeleteId, accessToken)
        await handleDeleteDataByOne('documents', documentToDeleteId)
      } catch (error: any) {
        console.log(error, error?.response?.data?.message)
        if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
          handleCloseSession()
        } else if (error?.response?.status === 401) {
          setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
        } else {
          setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
        }

        handleDiscardDeletionProcess()
        setDialogDeleteDocumentIsLoading(false)
        return
      }
    }

    handleDiscardDeletionProcess()
    setOpenDeleteDocumentModal(false)
    updateNewNotificationStatus?.(true, 'document-deleted')
  }

  const handleSearchBatchesCompletedDataByRegex = async () => {
    try {
      setErrors({})
      setDocumentsToDelete([])
      handleUncheckAllDocuments()
      setSearchingInDatabase(true)
      documentsToDeleteRef.current = []

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchBatchesByRegex({ searchText: documentToSearch, status: 'completed', belongsTo: userSession?.belongsTo, currency }, accessToken)

      if (data.batchesCompletedData.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      data.batchesCompletedData.sort((thisDocument: Partial<Document>, nextDocument: Partial<Document>) => {
        return Date.parse(nextDocument.createdAt) - Date.parse(thisDocument.createdAt)
      })
      setDocumentsList(data.batchesCompletedData)
      setSearchedDocuments(data.batchesCompletedData)
      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentDocumentsData = [...currentDocumentsDataRef.current]
      const documentsDataUpdated = upsertManyWithLimitAtStartFast(currentDocumentsData, data.batchesCompletedData, 500)
      documentsDataUpdated.sort((thisDocument: Partial<Document>, nextDocument: Partial<Document>) => {
        return Date.parse(nextDocument.createdAt) - Date.parse(thisDocument.createdAt)
      })

      currentDocumentsDataRef.current = documentsDataUpdated
      setDocumentsData(documentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)
      globalThis.sessionStorage.setItem(`batchesCompletedData:${userSession?.belongsTo}:${selectedCountry}`, JSON.stringify({ batchesCompletedData: documentsDataUpdated }))
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    if (removeAuth) {
      removeAuth()
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        aria-label="breadcrumb"
      >
        <Typography
          key="3"
          className="breadcrumb-inactive"
        >
          {t('breadcrumbs.home')}
        </Typography>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/customer-company-batches/${userSession?.belongsTo}`)}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </ListItemButton>
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.secondLevel')}
        </Typography>
      </Breadcrumbs>
      <ActionsBar
        errors={errors}
        documentToSearch={documentToSearch}
        searchingInDatabase={searchingInDatabase}
        generalActionChecked={selectAllDocuments}
        handleClearSearchInput={() => setErrors({})}
        showDeleteButton={showDeleteButtonFromActionsBar}
        processingDocument={processingBatch}
        handleSetDocumentToSearch={handleSetDocumentToSearch}
        handleOpenFiltersOptionsModal={() => setOpenFiltersOptionsModal(true)}
        documentsData={documentToSearch === '' ? documentsData : searchedDocuments}
        handleDialogOpenState={() => setOpenDeleteDocumentModal(!openDeleteDocumentModal)}
        handleToggleGeneralActionChecked={(checked) => {
          setSelectAllDocuments(checked)

          if (checked === true) {
            const currentDocumentsList = [...documentsList]
            const currentDocumentsDataStateUpdated = currentDocumentsDataRef.current
            const documentsListUpdated = currentDocumentsList.map((documentData) => ({
              ...documentData,
              selected: true
            }))
            const currentDocumentsDataIds = documentsListUpdated.map((documentData) => documentData._id)
            const filteredDocumentsToDelete = currentDocumentsDataIds.filter((id) => id !== undefined) as string[]
            currentShowDeleteButtonFromActionsBarRef.current = true
            documentsToDeleteRef.current = filteredDocumentsToDelete

            for (const element of documentsListUpdated) {
              const documentListData = element
              const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((documentData) => String(documentData._id) === String(documentListData._id)) ?? -1

              if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                currentDocumentsDataStateUpdated[findDocumentIndex].selected = true
              }
            }

            currentDocumentsDataRef.current = currentDocumentsDataStateUpdated

            setDocumentsList(documentsListUpdated)
            setShowDeleteButtonFromActionsBar(true)
            setDocumentsToDelete(filteredDocumentsToDelete)
            setDocumentsData(currentDocumentsDataStateUpdated)
          } else {
            const currentDocumentsList = [...documentsList]
            const currentDocumentsDataStateUpdated = currentDocumentsDataRef.current
            const documentsListUpdated = currentDocumentsList.map((documentData) => ({
              ...documentData,
              selected: false
            }))

            for (const element of documentsListUpdated) {
              const documentListData = element
              const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((documentData) => String(documentData._id) === String(documentListData._id)) ?? -1

              if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                currentDocumentsDataStateUpdated[findDocumentIndex].selected = false
              }
            }

            documentsToDeleteRef.current = []
            currentShowDeleteButtonFromActionsBarRef.current = false
            currentDocumentsDataRef.current = currentDocumentsDataStateUpdated

            handleUncheckAllDocuments()
            setDocumentsToDelete([])
            setDocumentsList(documentsListUpdated)
            setShowDeleteButtonFromActionsBar(false)
            setDocumentsData(currentDocumentsDataStateUpdated)
          }
        }}
      />
      {processingBatch && <ProcessingDocumentWarning />}
      <div className="table-header-container">
        <div className="documents-list-item-data-document-info table-header-text first-table-header">{t('tableHeaders.name')}</div>
        <div className="documents-list-item-status-container table-header-text">{t('tableHeaders.status')}</div>
        <div className="documents-list-item-actions-container table-header-text"></div>
      </div>
      {loadingLocalData ? (
        <div className="documents-list-container">
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
          <div className="documents-list-item-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="documents-list-container">
            {documentsList.map((documentData: Document) => {
              return (
                <DocumentsCompletedListItem
                  key={documentData._id}
                  userSession={userSession as UserSessionType}
                  documentData={documentData}
                  processingDocument={processingBatch || false}
                  handleGoToDocumentPaymentsDetails={() => {
                    if (documentData.status.includes('error')) {
                      navigate(`/document-details/${documentData._id}`, { state: { previousPath: 'documents-completed' } })
                      return
                    }

                    navigate(`/document-payments-details/${documentData._id}`, { state: { previousPath: 'documents-completed' } })
                  }}
                  handleToggleDocumentSelection={(selectedDocumentData: Document, selected: boolean) => {
                    let documentsSelected = 0
                    let documentsToDeleteUpdated = []
                    const currentDocumentsList = [...documentsList]
                    const currentDocumentsToDelete = [...documentsToDelete]
                    const currentDocumentsDataStateUpdated = currentDocumentsDataRef.current

                    const documentsListUpdated = currentDocumentsList.map((documentData) => {
                      if (String(documentData._id) === String(selectedDocumentData._id)) {
                        documentData.selected = selected
                      }

                      if (documentData.selected === true) documentsSelected++

                      return documentData
                    })

                    if (selected === true) {
                      documentsToDeleteUpdated = currentDocumentsToDelete.concat(selectedDocumentData._id ?? [])
                      documentsToDeleteRef.current = documentsToDeleteUpdated
                      setDocumentsToDelete(documentsToDeleteUpdated)

                      const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((documentData) => String(documentData._id) === String(selectedDocumentData._id)) ?? -1

                      if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                        currentDocumentsDataStateUpdated[findDocumentIndex].selected = true
                      }

                      currentDocumentsDataRef.current = currentDocumentsDataStateUpdated
                      setDocumentsData(currentDocumentsDataStateUpdated as Array<Document>)
                    } else {
                      const index = currentDocumentsToDelete.indexOf(selectedDocumentData._id ?? '')
                      if (index > -1) {
                        currentDocumentsToDelete.splice(index, 1)
                      }

                      documentsToDeleteUpdated = [...currentDocumentsToDelete]
                      documentsToDeleteRef.current = documentsToDeleteUpdated
                      setDocumentsToDelete(currentDocumentsToDelete)

                      const findDocumentIndex = currentDocumentsDataStateUpdated?.findIndex((customerData) => String(customerData._id) === String(selectedDocumentData._id)) ?? -1

                      if (findDocumentIndex !== -1 && currentDocumentsDataStateUpdated) {
                        currentDocumentsDataStateUpdated[findDocumentIndex].selected = false
                      }

                      currentDocumentsDataRef.current = currentDocumentsDataStateUpdated
                      setDocumentsData(currentDocumentsDataStateUpdated as Array<Document>)
                    }

                    if (documentsToDeleteUpdated.length > 0) {
                      currentShowDeleteButtonFromActionsBarRef.current = true
                      setShowDeleteButtonFromActionsBar(true)
                    } else {
                      currentShowDeleteButtonFromActionsBarRef.current = false
                      setShowDeleteButtonFromActionsBar(false)
                    }

                    if (documentsList.length === documentsSelected) {
                      setSelectAllDocuments(true)
                    } else {
                      setSelectAllDocuments(false)
                    }

                    setDocumentsList(documentsListUpdated)
                  }}
                  handleOpenDeleteDocumentDataModal={(selectedDocumentData: Document) => {
                    const documentsToDeleteUpdated = [selectedDocumentData._id] as Array<string>
                    const currentDocumentsList = [...documentsList]
                    const documentsListUpdated = currentDocumentsList.map((documentData) => ({
                      ...documentData,
                      selected: false
                    }))

                    handleUncheckAllDocuments()
                    setSelectAllDocuments(false)
                    setOpenDeleteDocumentModal(true)
                    setDocumentsList(documentsListUpdated)
                    currentShowDeleteButtonFromActionsBarRef.current = false
                    setShowDeleteButtonFromActionsBar(false)
                    documentsToDeleteRef.current = documentsToDeleteUpdated
                    setDocumentsToDelete(documentsToDeleteUpdated)
                    handleChangeStateToOpenModal(selectedDocumentData)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              handleUncheckAllDocuments()

              setTimeout(() => {
                setScrollToTop(true)
                documentsToDeleteRef.current = []
                setDocumentsToDelete([])
                setSelectAllDocuments(false)
                currentListPageRef.current = page
                setCurrentListPage(page)
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={documentToSearch === '' ? documentsData.length : searchedDocuments.length}
          />
          {noRegistriesFoundAtLocal && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase ? (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
                </>
              ) : (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchBatchesCompletedDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchBatchesCompletedDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {openFiltersOptionsModal && (
            <FiltersOptionsModal
              filtersToApply="batches"
              systemUsersData={systemUsersData}
              handleDialogOpenState={(openState: boolean) => {
                setOpenFiltersOptionsModal(openState)
              }}
              title={t('filtersOptionsModal.documentsToReview.title', { ns: 'common' })}
              actionButtonText={t('filtersOptionsModal.documentsToReview.actionButtonText', { ns: 'common' })}
            />
          )}
          {openDeleteDocumentModal && (
            <Dialog
              errors={errors}
              actionButtonWarningStyle={true}
              dialogIsLoading={dialogDeleteDocumentIsLoading}
              handleDialogAction={() => handleConfirmDocumentDeletion()}
              actionButtonText={t('deleteDocumentModal.actionButtonText')}
              dialogTitle={documentsToDelete.length === 1 ? t('deleteDocumentModal.title') : `${t('deleteDocumentModal.title')}s`}
              handleDialogOpenState={(openState: boolean) => {
                setOpenDeleteDocumentModal(openState)

                if (openState === false) {
                  setErrors({})
                  setSelectedDocument((currentSelectedDocumentState) => {
                    if (currentSelectedDocumentState.originalName) {
                      setDocumentsToDelete([])
                      documentsToDeleteRef.current = []
                    }

                    return {}
                  })
                }
              }}
            >
              <div className="dialog-content-text">
                {selectedDocument.originalName ? t('deleteDocumentModal.simpleDocumentToDeleteText', { documentOriginalName: selectedDocument.originalName }) : t('deleteDocumentModal.multipleDocumentToDeleteText')}
              </div>
            </Dialog>
          )}
        </>
      )}
    </div>
  )
}

export default DocumentsCompletedManagement
